import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './config/authConfig';
import Header from './components/Header';
import Footer from './components/Footer';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import OnboardingPage from './components/OnboardingPage';
import SageMakerTab from './components/SageMakerTab';
import axios from 'axios';

// API endpoint URL - replace with your actual API Gateway URL when deployed
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/dev';

// Add debug logging for API URL
console.log('Main App using API URL:', API_URL);

// Initialize MSAL
const msalInstance = new PublicClientApplication(msalConfig);
// Ensure MSAL is initialized
msalInstance.initialize().catch(error => {
  console.error("MSAL initialization failed:", error);
});

function App() {
  const [authProcessed, setAuthProcessed] = useState(false);

  useEffect(() => {
    const handleRedirectPromise = async () => {
      try {
        // Make sure MSAL is initialized before handling the redirect
        await msalInstance.initialize();
        
        const response = await msalInstance.handleRedirectPromise();
        // If we have a successful response, we got redirected back from Azure
        if (response) {
          console.log("Successfully logged in:", response);
          msalInstance.setActiveAccount(response.account);
        }
      } catch (error) {
        console.error("Failed to handle redirect:", error);
      } finally {
        setAuthProcessed(true);
      }
    };
    
    handleRedirectPromise();
    
    // Add event listener for login success
    const callbackId = msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        console.log("Login successful!");
        if (event.payload.account) {
          msalInstance.setActiveAccount(event.payload.account);
        }
      }
    });
    
    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  // Don't render routes until auth redirect is processed
  if (!authProcessed) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <p className="text-gray-600">Loading...</p>
    </div>;
  }

  return (
    <Router>
      <MsalProvider instance={msalInstance}>
        <div className="min-h-screen bg-gray-100">
          <Routes>
            {/* Onboarding page - default landing page */}
            <Route path="/" element={<OnboardingPage />} />
            
            {/* Login page */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Protected routes */}
            <Route path="/dashboard/*" element={
              <RequireAuth>
                <Layout>
                  <Routes>
                    <Route path="/" element={<MainContent />} />
                    <Route path="/sagemaker" element={<SageMakerPage />} />
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                </Layout>
              </RequireAuth>
            } />
            
            {/* Redirect any other path to onboarding */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </div>
      </MsalProvider>
    </Router>
  );
}

function RequireAuth({ children }) {
  const { accounts } = useMsal();
  
  if (accounts.length === 0) {
    console.log("No accounts found, redirecting to login");
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="container mx-auto px-4 py-8 flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function MainContent() {
  const [instances, setInstances] = useState([]);
  const [filteredInstances, setFilteredInstances] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showOnlyWorkspaces] = useState(true); // Always show only WS- instances, removed setter
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [synced, setSynced] = useState(false);
  // Add state for tracking instances being polled for status changes
  const [pollingInstances, setPollingInstances] = useState([]);
  
  // Update debug state to hide debug information
  const [debugInfo, setDebugInfo] = useState({
    apiUrl: API_URL,
    lastResponse: null,
    lastError: null,
    useLocalStorageData: false,
    showDebug: false
  });
  
  // Function to fetch instances directly from the multi-account endpoint
  const fetchInstances = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the multi-account endpoint for EC2 instance data from all accounts
      console.log('Fetching instances from multi-account endpoint:', `${API_URL}/multi-account-instances`);
      const response = await axios.get(`${API_URL}/multi-account-instances`, { 
        timeout: 30000,
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Instances response:', response.data);
      
      if (response.data && response.data.instances && response.data.instances.length > 0) {
        // Set all instances directly from the response
        setInstances(response.data.instances);
        
        // Set accounts information
        if (response.data.accounts) {
          setAccounts(response.data.accounts);
        }
        
        // Filter instances based on the showOnlyWorkspaces flag
        applyFilter(response.data.instances);
        
        setError(null);
      } else {
        // No instances found
        setInstances([]);
        setFilteredInstances([]);
        setError('No EC2 instances found. Please check your AWS accounts.');
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
      setInstances([]);
      setFilteredInstances([]);
      
      // Display appropriate error message
      if (error.message && error.message.includes('Network Error')) {
        setError('Network error while connecting to API. Please check your connectivity and CORS configuration.');
      } else if (error.code === 'ECONNABORTED') {
        setError('Connection timed out. The request took too long to complete.');
      } else if (error.response) {
        setError(`Server error: ${error.response.status} - ${error.response.statusText}`);
      } else {
        setError(`Error fetching instances: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Function to check specific instance status
  const checkInstanceStatus = async (instanceId, accountId, targetState) => {
    try {
      console.log(`Checking status of instance ${instanceId} (Account: ${accountId})`);
      
      // Use the multi-account endpoint to get the latest instance data
      const response = await axios.get(`${API_URL}/multi-account-instances`, {
        withCredentials: true,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.instances) {
        // Find the specific instance in the response
        const updatedInstance = response.data.instances.find(
          instance => instance.id === instanceId && instance.accountId === accountId
        );
        
        if (updatedInstance) {
          console.log(`Instance ${instanceId} current state:`, updatedInstance.state);
          
          // Update instances list with the latest data
          setInstances(prevInstances => 
            prevInstances.map(instance => 
              instance.id === instanceId && instance.accountId === accountId 
                ? updatedInstance 
                : instance
            )
          );
          
          // If instance has reached the target state (running or stopped), remove from polling list
          if (
            (targetState === 'running' && updatedInstance.state === 'running') || 
            (targetState === 'stopped' && updatedInstance.state === 'stopped')
          ) {
            console.log(`Instance ${instanceId} has reached target state: ${targetState}`);
            setPollingInstances(prevPolling => 
              prevPolling.filter(item => !(item.id === instanceId && item.accountId === accountId))
            );
            return true;
          } else if (
            (targetState === 'running' && updatedInstance.state === 'stopped') || 
            (targetState === 'stopped' && updatedInstance.state === 'running')
          ) {
            // If instance goes back to the opposite state, there was an error in the process
            console.log(`Instance ${instanceId} is not transitioning correctly to ${targetState}`);
            setPollingInstances(prevPolling => 
              prevPolling.filter(item => !(item.id === instanceId && item.accountId === accountId))
            );
            return false;
          }
        }
      }
      return false;
    } catch (error) {
      console.error(`Error checking instance ${instanceId} status:`, error);
      return false;
    }
  };
  
  // Apply filtering based on the showOnlyWorkspaces flag - simplified since we always filter
  const applyFilter = (allInstances) => {
    // Always filter instances to only include those with names starting with "WS-"
    const workspaceInstances = allInstances.filter(instance => 
      instance.name && instance.name.startsWith('WS-')
    );
    setFilteredInstances(workspaceInstances);
  };
  
  // Apply filter when instances change
  useEffect(() => {
    applyFilter(instances);
  }, [instances]);
  
  // Initial fetch of instances
  useEffect(() => {
    fetchInstances();
  }, []);
  
  // Effect for polling instance status changes
  useEffect(() => {
    // Skip if no instances are being polled
    if (pollingInstances.length === 0) return;
    
    // Set up polling interval (every 5 seconds)
    const pollingInterval = setInterval(async () => {
      console.log('Polling instance status for:', pollingInstances);
      
      // Create a copy of the polling instances to track completed polls
      const remainingPolls = [...pollingInstances];
      
      // Check each instance in the polling list
      for (const instance of pollingInstances) {
        await checkInstanceStatus(instance.id, instance.accountId, instance.targetState);
      }
    }, 5000);
    
    // Clean up interval on component unmount
    return () => clearInterval(pollingInterval);
  }, [pollingInstances]);

  // Function to start an instance
  const startInstance = async (instanceId, accountId) => {
    setLoading(true);
    console.log('Starting instance with ID:', instanceId);
    console.log('In account:', accountId);
    console.log('Using API URL:', API_URL);
    
    try {
      // Use the multi-account API endpoint
      console.log('Sending POST request to:', `${API_URL}/multi-account-instances/start`);
      console.log('Request payload:', JSON.stringify({ instanceId, accountId }));
      
      const response = await axios.post(`${API_URL}/multi-account-instances/start`, { instanceId, accountId }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Start instance response:', response.data);
      
      // Add instance to polling list to track status changes
      setPollingInstances(prev => [
        ...prev.filter(item => !(item.id === instanceId && item.accountId === accountId)),
        { id: instanceId, accountId, targetState: 'running', startTime: new Date() }
      ]);
      
      // Update the instance status to "pending" immediately for better UX
      setInstances(prevInstances => 
        prevInstances.map(instance => 
          instance.id === instanceId && instance.accountId === accountId 
            ? { ...instance, state: 'pending' } 
            : instance
        )
      );
      
      // Refresh the instance list once
      fetchInstances();
    } catch (err) {
      console.error('Error starting instance:', err);
      
      // More detailed error logging
      if (err.response) {
        console.error('Error response status:', err.response.status);
        console.error('Error response data:', JSON.stringify(err.response.data));
      } else if (err.request) {
        console.error('No response received. Request details available in browser console.');
      } else {
        console.error('Error setting up request:', err.message);
      }
      
      setError(`Failed to start instance: ${err.response?.data?.message || err.message}. Please try again.`);
      setLoading(false);
    }
  };

  // Function to stop an instance
  const stopInstance = async (instanceId, accountId) => {
    setLoading(true);
    console.log('Stopping instance with ID:', instanceId);
    console.log('In account:', accountId);
    
    try {
      // Use the multi-account API endpoint
      console.log('Sending POST request to:', `${API_URL}/multi-account-instances/stop`);
      console.log('Request payload:', JSON.stringify({ instanceId, accountId }));
      
      await axios.post(`${API_URL}/multi-account-instances/stop`, { instanceId, accountId }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      // Add instance to polling list to track status changes
      setPollingInstances(prev => [
        ...prev.filter(item => !(item.id === instanceId && item.accountId === accountId)),
        { id: instanceId, accountId, targetState: 'stopped', startTime: new Date() }
      ]);
      
      // Update the instance status to "stopping" immediately for better UX
      setInstances(prevInstances => 
        prevInstances.map(instance => 
          instance.id === instanceId && instance.accountId === accountId 
            ? { ...instance, state: 'stopping' } 
            : instance
        )
      );
      
      // Refresh the instance list once
      fetchInstances();
    } catch (err) {
      console.error('Error stopping instance:', err);
      setError(`Failed to stop instance: ${err.response?.data?.message || err.message}. Please try again.`);
      setLoading(false);
    }
  };
  
  return (
    <div className="container mx-auto p-4">
      <Dashboard 
        instances={filteredInstances}
        accounts={accounts}
        loading={loading}
        error={error}
        onRefresh={fetchInstances}
        onStart={startInstance}
        onStop={stopInstance}
        pollingInstances={pollingInstances}
        showOnlyWorkspaces={showOnlyWorkspaces}
        totalInstanceCount={instances.length}
      />
    </div>
  );
}

function SageMakerPage() {
  const [costs, setCosts] = useState(null);
  const [notebooks, setNotebooks] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [apps, setApps] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState({});
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    const requestConfig = {
      timeout: 30000,
      withCredentials: true,
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' }
    };

    try {
      const [costsRes, resourcesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/sagemaker/costs`, requestConfig),
        axios.get(`${API_URL}/sagemaker/resources`, requestConfig)
      ]);

      if (costsRes.status === 'fulfilled') {
        setCosts(costsRes.value.data);
      } else {
        console.error('Error fetching SageMaker costs:', costsRes.reason);
      }

      if (resourcesRes.status === 'fulfilled') {
        const data = resourcesRes.value.data;
        setNotebooks(data.notebooks || []);
        setEndpoints(data.endpoints || []);
        setApps(data.apps || []);
        setAccounts(data.accounts || []);
      } else {
        console.error('Error fetching SageMaker resources:', resourcesRes.reason);
      }

      if (costsRes.status === 'rejected' && resourcesRes.status === 'rejected') {
        setError('Failed to load SageMaker data. Please check API connectivity.');
      }
    } catch (err) {
      console.error('Error fetching SageMaker data:', err);
      setError(`Error fetching SageMaker data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const stopNotebook = async (notebookInstanceName, accountId, region) => {
    const key = `nb:${accountId}:${notebookInstanceName}`;
    setActionInProgress(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API_URL}/sagemaker/notebooks/stop`, { notebookInstanceName, accountId, region }, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });
      setNotebooks(prev => prev.map(nb =>
        nb.name === notebookInstanceName && nb.accountId === accountId
          ? { ...nb, status: 'Stopping' }
          : nb
      ));
    } catch (err) {
      console.error('Error stopping notebook:', err);
      setError(`Failed to stop notebook ${notebookInstanceName}: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionInProgress(prev => ({ ...prev, [key]: false }));
    }
  };

  const deleteEndpoint = async (endpointName, accountId, region) => {
    const key = `ep:${accountId}:${endpointName}`;
    setActionInProgress(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API_URL}/sagemaker/endpoints/delete`, { endpointName, accountId, region }, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });
      setEndpoints(prev => prev.map(ep =>
        ep.name === endpointName && ep.accountId === accountId
          ? { ...ep, status: 'Deleting' }
          : ep
      ));
    } catch (err) {
      console.error('Error deleting endpoint:', err);
      setError(`Failed to delete endpoint ${endpointName}: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionInProgress(prev => ({ ...prev, [key]: false }));
    }
  };

  const deleteApp = async (app) => {
    const key = `app:${app.accountId}:${app.region}:${app.name}`;
    setActionInProgress(prev => ({ ...prev, [key]: true }));
    try {
      await axios.post(`${API_URL}/sagemaker/apps/delete`, {
        accountId: app.accountId,
        region: app.region,
        domainId: app.domainId,
        appType: app.appType,
        appName: app.name,
        userProfileName: app.userProfileName,
        spaceName: app.spaceName
      }, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      });
      setApps(prev => prev.map(a =>
        a.name === app.name && a.accountId === app.accountId && a.region === app.region && a.appType === app.appType
          ? { ...a, status: 'Deleting' }
          : a
      ));
    } catch (err) {
      console.error('Error deleting app:', err);
      setError(`Failed to delete app ${app.name}: ${err.response?.data?.message || err.message}`);
    } finally {
      setActionInProgress(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="container mx-auto p-4">
      <SageMakerTab
        costs={costs}
        notebooks={notebooks}
        endpoints={endpoints}
        apps={apps}
        accounts={accounts}
        loading={loading}
        error={error}
        actionInProgress={actionInProgress}
        onRefresh={fetchData}
        onStopNotebook={stopNotebook}
        onDeleteEndpoint={deleteEndpoint}
        onDeleteApp={deleteApp}
      />
    </div>
  );
}

export default App; 